// index.js
import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import ErrorBoundary from './components/ErrorBoundary.js';
import ClimbingAnalysis from './components/ClimbingAnalysis.js';
import { loadGrades } from './data/grades.js';

const basePath = window.location.pathname.includes('/climbing-dashboard/')
  ? '/climbing-dashboard/'
  : '/';

// Reconstruct record format expected by the dashboard
// The export script uses short field names to minimize JSON size
const reconstructRecord = (record) => ({
  // Map short names back to original field names
  'Route ID': record.ri,
  'Route Name': record.rn,
  'Route Grade': record.rg,
  'Route Gear Style': record.rs,
  'Route Height': record.rh,
  'Crag ID': record.ci,
  'Crag Name': record.cn,
  'Country': record.co,
  'Ascent Date': record.ad,
  'Ascent Type': record.at,
  'Ascent Comment': record.ac,
  'Ascent Partners': record.ap,
  // Computed fields
  year: record.y,
  month: record.m,
  day: record.d,
  height: record.h,
  ewbanks: record.e,
  vgrade: record.v,
  isClean: record.ic,
  isFirstAttempt: record.if,
  attemptNumber: record.an,
  shouldCount: record.sc,
  is2ndGo: record.i2
});

// Parse URL parameters for locked controls
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    data: params.get('data'),
    style: params.get('style'),      // sport, trad, boulder
    grades: params.get('grades'),    // ewbank, french, yds
    year: params.get('year'),        // specific year or 'all'
    month: params.get('month'),      // specific month (1-12) or 'all'
    mode: params.get('mode'),        // 'poster' to lock in poster mode
    chart: params.get('chart'),      // single chart: grades, flash, progression, ascents, routesPerYear, crags, types, projects, cragPerformance, bestClimbs, partners, heatmap
    heading: params.get('heading'),  // optional heading override for single chart mode
  };
};

const App = () => {
  const [climbingData, setClimbingData] = useState(window.data || null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [loadedFromUrl, setLoadedFromUrl] = useState(false);
  const [climberName, setClimberName] = useState(null);
  const [urlParams] = useState(getUrlParams);

  const handleDataLoaded = (newData) => {
    window.data = newData;
    setClimbingData(newData);
  };

  // Check for URL parameter on mount
  useEffect(() => {
    const dataParam = urlParams.data;

    if (dataParam && !climbingData) {
      setIsLoading(true);
      setLoadError(null);

      const dataUrl = `${basePath}data/${dataParam}.json`;

      fetch(dataUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to load data: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          // Handle both old format (array) and new format (object with metadata)
          const ascents = Array.isArray(data) ? data : data.ascents;
          const climber = Array.isArray(data) ? null : data.climber;

          // Reconstruct records to expected format
          const reconstructedData = ascents.map(reconstructRecord);
          handleDataLoaded(reconstructedData);
          setClimberName(climber);
          setLoadedFromUrl(true);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error loading data:', error);
          setLoadError(error.message);
          setIsLoading(false);
        });
    }
  }, []);

  if (isLoading) {
    return (
      <div className="container mx-auto p-4 min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading climbing data...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="container mx-auto p-4 min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {loadError}</p>
          <p className="text-gray-600">
            Please check the URL or <a href="?" className="text-blue-600 underline">upload your own data</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className={`container mx-auto p-4 bg-gray-100 ${urlParams.chart ? '' : 'min-h-screen'}`}>
        <ClimbingAnalysis
          data={climbingData}
          onDataLoaded={handleDataLoaded}
          hideUpload={loadedFromUrl}
          climberName={climberName}
          lockedStyle={urlParams.style}
          lockedGradeSystem={urlParams.grades}
          lockedYear={urlParams.year}
          lockedMonth={urlParams.month}
          lockedMode={urlParams.mode}
          singleChart={urlParams.chart}
          singleChartHeading={urlParams.heading}
        />
      </div>
    </ErrorBoundary>
  );
};

const container = document.getElementById('root');
const root = createRoot(container);

loadGrades(`${basePath}data/grades.json`)
  .then(() => root.render(<App />))
  .catch(error => {
    console.error('Failed to load grade conversion data:', error);
    root.render(
      <div className="container mx-auto p-4 min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-2">Failed to load grade conversion data.</p>
          <p className="text-gray-600 text-sm">{error.message}</p>
        </div>
      </div>
    );
  });

export default App;
